import React, { useEffect, useMemo, useState } from 'react';
import { ServicePoint } from '../types';
import { usePos } from '../context/PosContext';
import { getUsb, isUsbAvailable, SavedPrinter, UsbDevice } from '../services/usbPrinter';
import { getKotPrinterMap, setKotPrinter, KotPrinterMap } from '../services/kotRouting';
import './Products.css';
import './Sections.css';

// Service Point master + Service-Point / Premise sales report
// (content_services_point_layout.xml + content_premise_layout.xml). Products
// carry a `servicePoint` name; each service point optionally rolls up to a
// premise. Reports aggregate sold items by the product's service point.
const ServicePoints: React.FC = () => {
  const { servicePoints, products, sales, addServicePoint, updateServicePoint, deleteServicePoint, formatMoney } = usePos();
  const [editing, setEditing] = useState<ServicePoint | null>(null);
  const [name, setName] = useState('');
  const [premise, setPremise] = useState('');
  const [view, setView] = useState<'sp' | 'premise'>('sp');

  // KOT printer routing: per-service-point USB printer assignment. Devices
  // come from the WebUSB authorized list (authorize new ones on USB Devices).
  const [usbDevices, setUsbDevices] = useState<UsbDevice[]>([]);
  const [printerMap, setPrinterMap] = useState<KotPrinterMap>({});
  useEffect(() => {
    getUsb()?.getDevices().then(setUsbDevices).catch(() => {});
    getKotPrinterMap().then(setPrinterMap);
  }, []);

  const deviceKey = (d: { vendorId: number; productId: number }) => `${d.vendorId}:${d.productId}`;
  const assignPrinter = async (spName: string, key: string) => {
    if (!key) {
      await setKotPrinter(spName, null);
    } else {
      const dev = usbDevices.find((d) => deviceKey(d) === key);
      if (!dev) return;
      const printer: SavedPrinter = {
        vendorId: dev.vendorId,
        productId: dev.productId,
        label: dev.productName || `USB ${deviceKey(dev)}`,
      };
      await setKotPrinter(spName, printer);
    }
    setPrinterMap(await getKotPrinterMap());
  };

  const productSpName = (pid: string) => products.find((p) => p.id === pid)?.servicePoint || '—';
  const premiseOf = (spName: string) => servicePoints.find((s) => s.name === spName)?.premise || '—';

  // Names used on products but not yet in the master (importable).
  const orphans = useMemo(() => {
    const known = new Set(servicePoints.map((s) => s.name.toLowerCase()));
    const used = new Set(products.map((p) => p.servicePoint).filter(Boolean) as string[]);
    return Array.from(used).filter((n) => !known.has(n.toLowerCase()));
  }, [servicePoints, products]);

  const productCount = (spName: string) => products.filter((p) => p.servicePoint === spName).length;

  // Completed-sale aggregation by service point (and premise rollup).
  const report = useMemo(() => {
    const completed = sales.filter((s) => s.status === 'completed' && s.total > 0);
    const sp = new Map<string, { qty: number; amount: number; tax: number }>();
    const prem = new Map<string, { qty: number; amount: number }>();
    completed.forEach((s) =>
      s.items.forEach((i) => {
        const spName = productSpName(i.productId);
        const r = sp.get(spName) ?? { qty: 0, amount: 0, tax: 0 };
        r.qty += i.quantity;
        r.amount += i.total;
        r.tax += i.total * (i.taxRatePercent / 100);
        sp.set(spName, r);

        const pName = premiseOf(spName);
        const pr = prem.get(pName) ?? { qty: 0, amount: 0 };
        pr.qty += i.quantity;
        pr.amount += i.total;
        prem.set(pName, pr);
      })
    );
    return { sp, prem };
  }, [sales, products, servicePoints]); // eslint-disable-line

  const reset = () => {
    setEditing(null);
    setName('');
    setPremise('');
  };

  const save = () => {
    if (!name.trim()) return alert('Service point name is required.');
    if (editing) updateServicePoint({ ...editing, name: name.trim(), premise: premise.trim() || undefined });
    else addServicePoint({ name: name.trim(), premise: premise.trim() || undefined });
    reset();
  };

  const edit = (s: ServicePoint) => {
    setEditing(s);
    setName(s.name);
    setPremise(s.premise ?? '');
  };

  const remove = () => {
    if (editing && window.confirm('Delete this service point?')) {
      deleteServicePoint(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <div className="pm-list-scroll">
          {servicePoints.map((s) => (
            <button key={s.id} className={`pm-list-item ${editing?.id === s.id ? 'sel' : ''}`} onClick={() => edit(s)}>
              <span className="pm-li-name">{s.name}</span>
              <span className="pm-li-meta">
                {s.premise ? `Premise: ${s.premise} · ` : ''}{productCount(s.name)} product{productCount(s.name) === 1 ? '' : 's'}
              </span>
            </button>
          ))}
          {servicePoints.length === 0 && <p className="muted" style={{ padding: 16 }}>No service points.</p>}

          {orphans.length > 0 && (
            <div style={{ padding: 12 }}>
              <div className="pf-lbl" style={{ marginBottom: 6 }}>Used on products — import:</div>
              {orphans.map((n) => (
                <button key={n} className="pf-btn-orange" style={{ margin: 3 }} onClick={() => addServicePoint({ name: n })}>
                  + {n}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* form + report */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>{editing ? 'Edit Service Point' : 'New Service Point'}</span>
          <div className="pf-top-btns">
            {editing && <button className="pf-btn-orange" onClick={reset}>New</button>}
            {editing && <button className="pf-btn-orange" onClick={remove}>Delete</button>}
            <button className="pf-btn-orange" onClick={save}>{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r2">
            <div>{lbl('Name *')}<input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div>{lbl('Premise')}<input value={premise} onChange={(e) => setPremise(e.target.value)} placeholder="e.g. Ground Floor" /></div>
          </div>

          <div className="pf-section">KOT Printer Routing</div>
          {!isUsbAvailable() ? (
            <p className="muted" style={{ fontSize: 12 }}>
              WebUSB is not available in this browser — KOTs print to the default printer.
            </p>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 12 }}>
                Assign a USB printer per station: a fired KOT is split by each line's service
                point and prints on that station's printer (unassigned lines use the default
                receipt printer). Authorize printers on the <b>USB Devices</b> screen first.
              </p>
              <table className="section-table" style={{ marginBottom: 10 }}>
                <thead>
                  <tr><th>Service Point</th><th>Printer</th></tr>
                </thead>
                <tbody>
                  {servicePoints.length === 0 ? (
                    <tr><td colSpan={2} className="muted">No service points yet.</td></tr>
                  ) : (
                    servicePoints.map((s) => (
                      <tr key={s.id}>
                        <td>{s.name}</td>
                        <td>
                          <select
                            value={printerMap[s.name] ? deviceKey(printerMap[s.name]) : ''}
                            onChange={(e) => assignPrinter(s.name, e.target.value)}
                          >
                            <option value="">Default printer</option>
                            {usbDevices.map((d) => (
                              <option key={deviceKey(d)} value={deviceKey(d)}>
                                {d.productName || `USB ${deviceKey(d)}`}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}

          <div className="pf-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Sales Report</span>
            <span>
              <button className={view === 'sp' ? 'btn-teal' : 'btn-grey'} style={{ marginRight: 6 }} onClick={() => setView('sp')}>Service Point Wise</button>
              <button className={view === 'premise' ? 'btn-teal' : 'btn-grey'} onClick={() => setView('premise')}>Premise Wise</button>
            </span>
          </div>

          <table className="section-table">
            <thead>
              {view === 'sp' ? (
                <tr><th>Service Point</th><th className="right">Qty</th><th className="right">Tax</th><th className="right">Amount</th></tr>
              ) : (
                <tr><th>Premise</th><th className="right">Qty</th><th className="right">Amount</th></tr>
              )}
            </thead>
            <tbody>
              {view === 'sp' ? (
                report.sp.size === 0 ? (
                  <tr><td colSpan={4} className="muted">No sales yet.</td></tr>
                ) : (
                  Array.from(report.sp.entries()).sort((a, b) => b[1].amount - a[1].amount).map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td className="right">{v.qty}</td><td className="right">{formatMoney(v.tax)}</td><td className="right">{formatMoney(v.amount)}</td></tr>
                  ))
                )
              ) : report.prem.size === 0 ? (
                <tr><td colSpan={3} className="muted">No sales yet.</td></tr>
              ) : (
                Array.from(report.prem.entries()).sort((a, b) => b[1].amount - a[1].amount).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td className="right">{v.qty}</td><td className="right">{formatMoney(v.amount)}</td></tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServicePoints;
